import axios from "axios";
import { CONFIG, authHostForToken, PROD_ADMIN_SERVER_URL } from "../../config";
import { getTokenizedAxios } from "../../utils/axiosWithTokenGen";
import { AccessLevel } from "../../interfaces/accessLevel.enum";
import { PlatformType } from "../../interfaces/paxTerminal.interface";
import { EmployeeType } from "../../interfaces/klearlyAdmin.interface";

export async function signInServer(props: {
  email: string;
  password: string;
}): Promise<{
  session?: string;
  qrcode?: string;
  mfaSetupAccessToken?: string;
}> {
  const axiosWithToken = await getTokenizedAxios();
  const { email, password } = props;

  const path = "/account/signInLemonPayAdmin";
  return axiosWithToken
    .post(CONFIG.ADMIN_SERVER.BASE_URL + path, { email, password })
    .then((data) => data?.data);
}

export async function verifySoftwareTokenServer(props: {
  session?: string;
  accessToken?: string;
  confirmationCode: string;
}): Promise<{
  session?: string;
  status: "SUCCESS" | "ERROR";
}> {
  const { session, accessToken, confirmationCode } = props;

  const path = "/account/verifySoftwareToken";
  return axios
    .post(CONFIG.ADMIN_SERVER.BASE_URL + path, {
      session,
      accessToken,
      confirmationCode,
    })
    .then((data) => data.data);
}
export async function respondToSignInServer(props: {
  session: string;
  confirmationCode: string;
  username: string;
  deviceId: string;
}): Promise<
  | {
      tokens: { idToken: string; accessToken?: string; refreshToken: string };
      accessLevel: AccessLevel[];
      couplingPlatformAccess: PlatformType | undefined;
      user: {
        email: string;
        name: string;
        partnerId?: string;
        employeeType?: EmployeeType;
      };
    }
  | { session: string }
> {
  const { session, confirmationCode, username, deviceId } = props;

  const path = "/account/respondToSignInAdminChallenge";
  return axios
    .post(CONFIG.ADMIN_SERVER.BASE_URL + path, {
      session,
      confirmationCode,
      username,
      deviceId,
    })
    .then((data) => data.data)
    .catch((error) => {
      throw error?.response?.data;
    });
}

export function refreshTokenSignInServer(props: {
  refreshToken: string;
  idToken?: string;
}): Promise<{
  idToken: string;
  accessToken?: string;
  accessLevel: AccessLevel[];
  couplingPlatformAccess: PlatformType | undefined;
  user: {
    email: string;
    name: string;
    partnerId?: string;
    employeeType?: EmployeeType;
  };
}> {
  const { refreshToken, idToken } = props;
  const path = "/account/refreshTokenSignIn";
  return axios
    .post(authHostForToken(idToken) + path, {
      refreshToken,
      isAdmin: true,
    })
    .then((data) => data?.data);
}
export async function logoutServer(props: {
  refreshToken: string;
  idToken?: string;
}): Promise<{}> {
  const { refreshToken, idToken } = props;
  const axios = await getTokenizedAxios();
  const path = "/account/logout";
  return axios
    .post(authHostForToken(idToken) + path, {
      refreshToken,
      isAdmin: true,
    })
    .then((data) => data.data);
}

export async function createEnvHandoffServer(props: {
  targetEnv: string;
  refreshToken: string;
  idToken: string;
}): Promise<{ code: string; expiresInSeconds: number }> {
  const { targetEnv, refreshToken, idToken } = props;
  const path = "/account/createEnvHandoff";
  return axios
    .post(
      authHostForToken(idToken) + path,
      { targetEnv, refreshToken },
      { headers: { Authorization: idToken } }
    )
    .then((data) => data.data);
}

export async function redeemEnvHandoffServer(props: { code: string }): Promise<{
  tokens: { idToken: string; accessToken?: string; refreshToken: string };
  accessLevel: AccessLevel[];
  couplingPlatformAccess: PlatformType | undefined;
  user: {
    email: string;
    name: string;
    partnerId?: string;
    employeeType?: EmployeeType;
  };
}> {
  const { code } = props;
  const path = "/account/redeemEnvHandoff";
  return axios
    .post(PROD_ADMIN_SERVER_URL + path, { code })
    .then((data) => data.data)
    .catch((error) => {
      throw error?.response?.data;
    });
}

type AdminSessionResponse = {
  tokens: { idToken: string; accessToken: string; refreshToken: string };
  accessLevel: AccessLevel[];
  couplingPlatformAccess: PlatformType | undefined;
  user: {
    email: string;
    name: string;
    partnerId?: string;
    employeeType?: EmployeeType;
  };
};

export type WebAuthnCredentialSummary = {
  credentialId: string;
  friendlyCredentialName?: string;
  createdAt?: string;
  relyingPartyId?: string;
};

export async function webauthnSignInStartServer(props: {
  email: string;
}): Promise<
  { session: string; credentialRequestOptions: unknown } | { available: false }
> {
  const path = "/account/webauthn/signInStart";
  return axios
    .post(CONFIG.ADMIN_SERVER.BASE_URL + path, { email: props.email })
    .then((data) => data.data);
}

export async function webauthnSignInCompleteServer(props: {
  email: string;
  session: string;
  credential: string;
}): Promise<AdminSessionResponse> {
  const path = "/account/webauthn/signInComplete";
  return axios
    .post(CONFIG.ADMIN_SERVER.BASE_URL + path, props)
    .then((data) => data.data)
    .catch((error) => {
      throw error?.response?.data;
    });
}

export async function webauthnRegisterStartServer(props: {
  idToken: string;
  accessToken: string;
}): Promise<{ credentialCreationOptions: unknown }> {
  const path = "/account/webauthn/registerStart";
  return axios
    .post(
      authHostForToken(props.idToken) + path,
      {},
      { headers: { Authorization: `Bearer ${props.accessToken}` } }
    )
    .then((data) => data.data);
}

export async function webauthnRegisterCompleteServer(props: {
  idToken: string;
  accessToken: string;
  credential: string;
  friendlyCredentialName?: string;
}): Promise<{ ok: boolean }> {
  const path = "/account/webauthn/registerComplete";
  return axios
    .post(
      authHostForToken(props.idToken) + path,
      {
        credential: props.credential,
        friendlyCredentialName: props.friendlyCredentialName,
      },
      { headers: { Authorization: `Bearer ${props.accessToken}` } }
    )
    .then((data) => data.data);
}

export async function webauthnListCredentialsServer(props: {
  idToken: string;
  accessToken: string;
}): Promise<{ credentials: WebAuthnCredentialSummary[] }> {
  const path = "/account/webauthn/credentials";
  return axios
    .get(authHostForToken(props.idToken) + path, {
      headers: { Authorization: `Bearer ${props.accessToken}` },
    })
    .then((data) => data.data);
}

export async function webauthnDeleteCredentialServer(props: {
  idToken: string;
  accessToken: string;
  credentialId: string;
}): Promise<{ ok: boolean }> {
  const path = `/account/webauthn/credentials/${encodeURIComponent(
    props.credentialId
  )}`;
  return axios
    .delete(authHostForToken(props.idToken) + path, {
      headers: { Authorization: `Bearer ${props.accessToken}` },
    })
    .then((data) => data.data);
}
